import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { todayLocalISO } from '../../utils/formatters';

interface OffboardingFieldsProps {
  offboardingDate: string;
  offboardingReason: string;
  onDateChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  idPrefix?: string;
  testIdPrefix?: string;
  showDateHelper?: boolean;
  reasonAsTextarea?: boolean;
}

/**
 * Shared off-boarding date + reason inputs, used by the deactivation dialog and
 * both person edit surfaces. The date is back-datable but capped at today; a
 * blank reason clears the stored one on an inactive member.
 */
export const OffboardingFields: React.FC<OffboardingFieldsProps> = ({
  offboardingDate,
  offboardingReason,
  onDateChange,
  onReasonChange,
  idPrefix = 'offboarding',
  testIdPrefix,
  showDateHelper = false,
  reasonAsTextarea = false,
}) => {
  const { t } = useTranslation('people');

  const reasonProps = {
    id: `${idPrefix}-reason`,
    maxLength: 500,
    value: offboardingReason,
    placeholder: t('offboarding.reasonPlaceholder'),
    'data-testid': testIdPrefix ? `${testIdPrefix}-input-reason` : undefined,
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-date`}>{t('offboarding.dateLabel')}</Label>
        <Input
          id={`${idPrefix}-date`}
          type="date"
          value={offboardingDate}
          max={todayLocalISO()}
          onChange={(e) => onDateChange(e.target.value)}
          data-testid={testIdPrefix ? `${testIdPrefix}-input-date` : undefined}
        />
        {showDateHelper && (
          <p className="text-xs text-muted-foreground">{t('offboarding.dateHelper')}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-reason`}>{t('offboarding.reasonLabel')}</Label>
        {reasonAsTextarea ? (
          <Textarea
            {...reasonProps}
            rows={3}
            onChange={(e) => onReasonChange(e.target.value)}
          />
        ) : (
          <Input {...reasonProps} onChange={(e) => onReasonChange(e.target.value)} />
        )}
      </div>
    </>
  );
};
